import { Link } from "react-router-dom";
import { HELP, HELP_THEME } from "@/help/helpContent";

const Anchor = ({ href, children }) =>
  href.startsWith("/") ? (
    <Link to={href} className={HELP_THEME.link}>{children}</Link>
  ) : (
    <a href={href} className={HELP_THEME.link} target={href.startsWith("mailto:") ? undefined : "_blank"} rel="noreferrer">{children}</a>
  );

// Renders "text [label](href) more" with in-app or external links. Content is static, so no HTML injection.
function Rich({ text }) {
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<Anchor key={m.index} href={m[2]}>{m[1]}</Anchor>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/**
 * Public user guide and FAQ (/help). Works before sign-in: it reads no account, calls no API
 * and shows no user data. Everything comes from the static content in help/helpContent.js.
 */
export default function UserGuide() {
  const t = HELP_THEME;
  return (
    <div className={t.page} data-testid="user-guide-page">
      <h1 className={t.h1}>{HELP.appName} user guide</h1>
      <p className={t.text}>{HELP.intro}</p>

      <nav className={`${t.card} mt-6`} aria-label="Guide contents" data-testid="help-toc">
        <div className={t.h3}>On this page</div>
        <ul className="mt-2 grid sm:grid-cols-2 gap-1">
          <li><a href="#getting-started" className={t.link}>Getting started</a></li>
          {HELP.features.map((f) => (
            <li key={f.id}><a href={`#${f.id}`} className={t.link}>{f.title}</a></li>
          ))}
          <li><a href="#faq" className={t.link}>FAQ</a></li>
        </ul>
      </nav>

      <section id="getting-started" className={`${t.card} mt-6`} data-testid="help-getting-started">
        <h2 className={t.h2}>Getting started</h2>
        <ol className="mt-3 list-decimal pl-5 space-y-2">
          {HELP.gettingStarted.map((s, i) => <li key={i} className={t.text}><Rich text={s} /></li>)}
        </ol>
      </section>

      <h2 className={`${t.h2} mt-10`}>Features</h2>
      {HELP.features.map((f) => (
        <section key={f.id} id={f.id} className={`${t.card} mt-4`} data-testid={`help-feature-${f.id}`}>
          <h3 className={t.h3}>{f.title}{f.comingSoon && <span className={t.badge}>Coming soon</span>}</h3>
          <p className={`${t.text} mt-2`}><strong>What it does:</strong> <Rich text={f.what} /></p>
          <p className={`${t.text} mt-2`}><strong>Why it helps:</strong> <Rich text={f.why} /></p>
          {f.steps?.length > 0 && (
            <>
              <p className={`${t.text} mt-2`}><strong>How to use it:</strong></p>
              <ol className="mt-1 list-decimal pl-5 space-y-1">
                {f.steps.map((s, i) => <li key={i} className={t.text}><Rich text={s} /></li>)}
              </ol>
            </>
          )}
        </section>
      ))}

      <section id="faq" className="mt-10" data-testid="help-faq">
        <h2 className={t.h2}>Frequently asked questions</h2>
        {HELP.faq.map((group) => (
          <div key={group.topic} className="mt-5" data-testid={`help-faq-${group.id}`}>
            <h3 className={t.h3}>{group.topic}</h3>
            {group.items.map((q) => (
              <details key={q.q} className={`${t.card} mt-2`}>
                <summary className={`${t.text} cursor-pointer font-semibold`}>{q.q}</summary>
                <p className={`${t.text} mt-2`}><Rich text={q.a} /></p>
              </details>
            ))}
          </div>
        ))}
      </section>

      <section className={`${t.card} mt-10`} data-testid="help-contact">
        <h2 className={t.h2}>Still need help?</h2>
        <p className={`${t.text} mt-2`}>
          Email us at <Anchor href={`mailto:${HELP.supportEmail}`}>{HELP.supportEmail}</Anchor>
          {HELP.supportRoute && <> or <Anchor href={HELP.supportRoute}>open a support ticket</Anchor> after signing in</>}.
          {" "}{HELP.appName} is run by {HELP.companyName}.
        </p>
      </section>
    </div>
  );
}
